<!--
  internal-checkout.vue
  
  2025-06-04: Fixed card field mounting issue by checking correct bankTransfer property and using $nextTick, fixed CSS syntax errors
-->
<template>
  <div class="internal-checkout">
    <div class="d-flex flex-column justify-center align-center">
      <v-form
        class="internal-checkout-form"
        ref="save"
        lazy-validation
        @submit.prevent="save"
      >
        <div class="font-weight-semibold py-4">Guest Details:</div>
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="payment.first_name"
              name="first_name"
              tabindex="1"
              label="First Name"
              class="text-body-2"
              type="text"
              outlined
              dense
              hide-details
              :rules="[required]"
              required
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="payment.last_name"
              name="last_name"
              tabindex="1"
              label="Last Name"
              type="text"
              outlined
              dense
              hide-details
              :rules="[required]"
              required
            />
          </v-col>
          <v-col cols="12" sm="12" md="12">
            <phone-input
              v-model="payment.phone"
              :phone-object.sync="phoneObject"
              :hideDetails="true"
            />
          </v-col>
          <v-col cols="12" sm="12" md="12">
            <v-text-field
              v-model="payment.email"
              name="email"
              tabindex="1"
              label="Email"
              type="text"
              outlined
              dense
              :rules="emailRules"
            />
          </v-col>
          <v-col cols="12" sm="12" md="12" v-if="bankSetting">
            <v-checkbox
              v-model="bankTransfer"
              :disabled="bankTransferFlow"
              class="checkbox mt-0"
              :label="$t('bank_transfer')"
              hide-details
            ></v-checkbox>
          </v-col>
          <template v-if="!bankTransfer">
            <v-col cols="12" sm="12" md="12">
              <div class="label">{{ $t("credit_card_information") }}</div>
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <div id="cardholder" class="checkout_form"></div>
            </v-col>
            <v-col cols="12" sm="6" md="12">
              <div id="cardnum" class="checkout_form"></div>
            </v-col>
            <v-col cols="6" sm="6" md="6">
              <div id="pincode" class="checkout_form"></div>
            </v-col>
            <v-col cols="6" sm="6" md="6">
              <div id="expiration" class="checkout_form"></div>
            </v-col>
            <v-col cols="12" sm="12" md="12" class="py-0 d-flex justify-end">
              <img
                src="@/assets/images/secure_visa.png"
                alt="Secure payment with Visa"
                class="secure-payment-logo"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12" class="py-0 d-flex justify-start">
              <div class="error-msg text-caption error--text">
                {{ errorMsg }}
              </div>
            </v-col>
          </template>
          <v-col cols="12">
            <v-col cols="12" class="px-0" v-if="settings.cancellation_policy">
              <v-divider class="mb-2" />
              <div class="font-weight-semibold py-2">
                {{ $t("cancellation_policy") }}:
              </div>
              <div class="text-body-2 grey--text text--darken-3 text-caption">
                {{ settings.cancellation_policy }}
              </div>
            </v-col>
            <v-divider class="mt-2" />
          </v-col>
          <v-col cols="12" class="p-relative">
            <div class="px-6">
              <v-checkbox
                v-model="termsAccepted"
                class="checkbox-txt mt-0"
                :color="actionsColor"
                style="position: absolute; left: 3px"
              ></v-checkbox>
              <div
                class="custom-terms-of-use text-caption"
                v-if="settings.custom_terms_of_use"
                v-html="settings.custom_terms_of_use"
              ></div>
              <div
                v-if="listing && !settings.custom_terms_of_use"
                class="d-flex terms-section py-0 pt-2"
              >
                <listing-terms-info
                  :listingId="this.reservationData.listing_id"
                  :range="this.reservationData.range"
                />
              </div>
            </div>
            <v-divider class="mt-2" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="12" md="12">
            <div class="d-flex justify-end align-baseline">
              <v-btn
                large
                block
                :color="actionsColor"
                type="submit"
                depressed
                class="white--text"
                :loading="isLoading"
                :disabled="!termsAccepted && !bankTransfer"
              >
                {{ $t("confirm") }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DeviceMixin from "@/mixins/device-mixin";
import FormRules from "@/mixins/form-rules-mixin";
import FormattersMixin from "@/mixins/formatters-mixin";
import { BasisTheory } from "@basis-theory/basis-theory-js";
import { axios } from "@/store/fetch";
import ListingTermsInfo from "@/components/listing/listing-terms-info";
import stylesMixin from "@/mixins/styles-mixin";
import PhoneInput from "@/components/common/phone-input.vue";

export default {
  name: "internal-checkout",
  components: {
    ListingTermsInfo,
    PhoneInput,
  },
  props: ["reservationData", "currentPricing", "list", "bankTransferFlow"],
  mixins: [DeviceMixin, FormattersMixin, FormRules, stylesMixin],

  data() {
    return {
      phoneObject: null,
      btPublic: null,
      cardTextElement: "",
      pincodeTextElement: "",
      expirationTextElement: "",
      cardholderTextElement: "",
      cardNumber: "",
      cardNumberFormatted: "",
      isLoading: false,
      termsAccepted: false,
      bankTransfer: this.bankTransferFlow,
      errorMsg: "",
      cardFieldsValid: {
        cardNumber: false,
        cvc: false,
        expiration: false,
        cardholder: false,
      },
      payment: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
      },
    };
  },

  async mounted() {
    this.btPublic = await new BasisTheory().init(
      this.settings.bt_public_api_key,
      { elements: true }
    );
    await this.btPublic.sessions.create();

    if (!this.bankTransfer) {
      this.$nextTick(() => {
        this.initiateCardFields();
      });
    }
  },
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),
    ...mapState("listingsModule", ["currentListing"]),
    listing() {
      return this.list;
    },
    bankSetting() {
      return window.config?.allow_bank_payments;
    },
    formatReservationPeriod() {
      return `${this.parseDate(this.reservationData.range[0], "MMM Do")}
        - ${this.parseDate(this.reservationData.range[1], "MMM Do")} `;
    },
  },
  watch: {
    bankTransfer(value) {
      if (!value) {
        this.$nextTick(() => {
          this.initiateCardFields();
        });
      }
    },
  },
  methods: {
    async save() {
      if (!this.$refs.save.validate() || !this.validateCardFields()) {
        const validCard = this.validateCardFields();
        this.errorMsg = validCard ? null : this.$t("credit_card_information");
        return;
      }
      this.isLoading = true;
      let token = null;
      if (!this.bankTransfer) {
        token = await this.btPublic.tokens.create({
          type: "token",
          data: {
            cardholder: this.cardholderTextElement,
            card: this.cardTextElement,
            pincode: this.pincodeTextElement,
            expiration: this.expirationTextElement,
          },
        });
      }
      const payload = {
        range: this.reservationData.range,
        first_name: this.payment.first_name,
        last_name: this.payment.last_name,
        phone: this.payment.phone,
        email: this.payment.email,
        fare_accommodation: this.currentPricing.fare_accommodation,
        guests_count: this.reservationData.guests_count,
        source: this.reservationData.source,
        listing_id: this.reservationData.listing_id,
        tenant_id: this.reservationData.tenant_id,
        tokenizer: this.reservationData.tokenizer,
        fare_cleaning: this.currentPricing.cleaning_fee,
        tax_lines: this.currentPricing.taxes_breakdown,
        fees_items: this.currentPricing.fees_breakdown,
        total: this.currentPricing.total,
        discount_code_id: this.currentPricing?.discount_code_id,
        rate_plan_id: this.reservationData?.rate_plan_id,
      };

      try {
        const res = await axios.post("/create-reservation", {
          token_id: token?.id,
          ...payload,
        });
        this.isLoading = false;
        if (res && res.status) {
          if (res.status === 200) {
            window.location.href = "/reservation-processed";
          }
        }
      } catch (error) {
        this.isLoading = false;
      }
    },
    initiateCardFields() {
      this.cardholderTextElement = this.btPublic.createElement("text", {
        targetId: "cardholder",
        placeholder: "Cardholder Name",
      });

      this.cardTextElement = this.btPublic.createElement("cardNumber", {
        targetId: "cardnum",
        placeholder: "1234 1234 1234 1234",
      });

      this.pincodeTextElement = this.btPublic.createElement(
        "cardVerificationCode",
        {
          targetId: "pincode",
          placeholder: "CVC",
        }
      );

      this.expirationTextElement = this.btPublic.createElement(
        "cardExpirationDate",
        {
          targetId: "expiration",
          placeholder: "MM/YY",
        }
      );

      // Add validation listeners
      this.cardholderTextElement.on("change", (e) => {
        this.cardFieldsValid.cardholder = e.complete;
      });

      this.cardTextElement.on("change", (e) => {
        this.cardFieldsValid.cardNumber = e.complete;
      });

      this.pincodeTextElement.on("change", (e) => {
        this.cardFieldsValid.cvc = e.complete;
      });

      this.expirationTextElement.on("change", (e) => {
        this.cardFieldsValid.expiration = e.complete;
      });

      this.cardholderTextElement.mount("#cardholder");
      this.cardTextElement.mount("#cardnum");
      this.pincodeTextElement.mount("#pincode");
      this.expirationTextElement.mount("#expiration");
    },
    validateCardFields() {
      if (this.bankTransfer) return true;
      return (
        this.cardFieldsValid.cardholder &&
        this.cardFieldsValid.cardNumber &&
        this.cardFieldsValid.cvc &&
        this.cardFieldsValid.expiration
      );
    },
  },
};
</script>
<style scoped>
:deep(.custom-terms-of-use a) {
  color: #000000;
}

:deep(.custom-terms-of-use p) {
  margin-bottom: 0px;
}

.internal-checkout .checkout_form {
  border: 1px solid grey;
  border-radius: 4px;
  line-height: 26px;
  min-height: 26px;
  padding: 5px;
}

.internal-checkout .router-link-active {
  margin-right: auto;
}

.internal-checkout .btn {
  margin-left: auto;
}

.internal-checkout #text {
  color: red;
}

.internal-checkout .label {
  font-size: 16px;
}

.internal-checkout .MuiInput-input {
  min-height: 36px;
  padding: 5px;
}

.internal-checkout .secure-payment-logo {
  max-height: 40px;
}

.internal-checkout :deep(.vue-tel-input) {
  border-color: #00000061 !important;
}
</style>
